import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { CheckCircle, Loader, CreditCard, AlertTriangle } from "lucide-react";
import { REACT_APP_RAZORPAY_KEY_ID } from "utils/constant";
const SubscriptionCheckout = ({ selectedPlan, onClose, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subscriptionCreated, setSubscriptionCreated] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState(null);

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => {
          resolve(true);
        };
        script.onerror = () => {
          resolve(false);
        };
        document.body.appendChild(script);
      });
    };

    const loadScript = async () => {
      const res = await loadRazorpayScript();
      if (!res) {
        setError(
          "Razorpay SDK failed to load. Please check your internet connection."
        );
      }
    };

    loadScript();
  }, []);

  const handleCreateSubscription = async () => {
    try {
      setLoading(true);
      setError(null);

      const accessToken = localStorage.getItem("access");

      // Step 1: Create the subscription
      const createResponse = await axios.post(
        `${BASE_URL}/subscription/create-subscription/`,
        {
          subscription_plan_id: selectedPlan.planId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!createResponse.data.success) {
        throw new Error(
          createResponse.data.message || "Failed to create subscription"
        );
      }

      setSubscriptionCreated(true);

      // Step 2: Get user subscription details with payment link
      const organizationId =
        JSON.parse(localStorage.getItem("organization"))?.id || null;

      if (!organizationId) {
        throw new Error("Organization ID not found");
      }

      const subscriptionResponse = await axios.post(
        `${BASE_URL}/subscription/get-user-subscription/`,
        {
          organization_id: organizationId,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!subscriptionResponse.data.status) {
        throw new Error(
          subscriptionResponse.data.message || "Failed to get payment details"
        );
      }

      setSubscriptionData(subscriptionResponse.data.data);
    } catch (err) {
      console.error("Subscription checkout error:", err);
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = () => {
    if (!subscriptionData || !subscriptionData.razorpay_subscription) {
      setError("Payment information not available. Please try again.");
      return;
    }

    if (!window.Razorpay) {
      setError(
        "Razorpay SDK not loaded. Please refresh the page and try again."
      );
      return;
    }

    // Get user details
    const userDetails = JSON.parse(localStorage.getItem("user")) || {};
    // console.log("userDetails", userDetails);
    const organizationDetails =
      JSON.parse(localStorage.getItem("organization")) || {};

    const options = {
      key: REACT_APP_RAZORPAY_KEY_ID || "rzp_test_key", // Use your environment variable
      subscription_id: subscriptionData.razorpay_subscription,
      name: "SiteMaster",
      description: `${selectedPlan.name} - ${selectedPeriod} Plan`,
      image: "../assets/SiteMaster.svg", // Your company logo
      handler: function (response) {
        // Handle successful payment
        // console.log("Payment successful:", response);

        // You might want to send these details to your backend
        const paymentData = {
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_subscription_id: response.razorpay_subscription_id,
          razorpay_signature: response.razorpay_signature,
        };

        // Show success message or redirect
        onPaymentSuccess(paymentData);
      },
      prefill: {
        name: userDetails.first_name + userDetails?.last_name || "",
        email: userDetails.user_email || "",
        contact: userDetails.contact_number || "",
      },
      notes: {
        organization_id: organizationDetails.id || "",
        subscription_plan_id: selectedPlan.planId,
      },
      theme: {
        color: "#7c3aed", // Purple color matching your UI
      },
    };

    const razorpayInstance = new window.Razorpay(options);
    razorpayInstance.open();
  };

  const onPaymentSuccess = async (paymentData) => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");

      // Validate the payment with the backend
      const validateResponse = await axios.post(
        `${BASE_URL}/subscription/validate-payment/`,
        {
          subscription_id: subscriptionData.subscription_id, // Use the Django subscription ID
          razorpay_payment_id: paymentData.razorpay_payment_id,
          razorpay_signature: paymentData.razorpay_signature,
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      // If validation is successful
      if (validateResponse.data.success) {
        // Display success message
        setPaymentSuccess(true);

        // Optional: You might want to update local storage or context
        // with the new subscription details
        const subscriptionDetails = validateResponse.data.subscription_details;
        localStorage.setItem(
          "subscription_details",
          JSON.stringify(subscriptionDetails)
        );

        // Close the modal after a delay
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        // Handle validation failure
        setError(validateResponse.data.message || "Payment validation failed");
      }
    } catch (err) {
      console.error("Payment validation error:", err);
      setError(
        err.response?.data?.error ||
          "Payment validation failed. Please contact support."
      );
    } finally {
      setLoading(false);
    }
  };

  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("");

  // Get period name from plan
  useEffect(() => {
    if (selectedPlan) {
      // Extract period from plan name or ID logic here
      // For now using a placeholder
      const periodMatch = subscriptionData?.subscription_name?.match(
        /(Monthly|Yearly|4 months|6 months)/i
      );
      setSelectedPeriod(periodMatch ? periodMatch[0] : "");
    }
  }, [selectedPlan, subscriptionData]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        // onClick={onClose}
      />

      {/* Modal Content */}
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl m-4 overflow-hidden">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={subscriptionCreated || paymentSuccess}
          className={`absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors ${
            subscriptionCreated && !paymentSuccess
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          <svg
            className="w-6 h-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Back button */}
        {!paymentSuccess && (
          <button
            onClick={onBack}
            className="absolute top-4 left-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
            disabled={loading || subscriptionCreated}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
        )}

        {subscriptionCreated && !paymentSuccess && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 flex items-center absolute top-0 left-0 right-0 z-10">
            <AlertTriangle className="text-yellow-600 mr-3" size={24} />
            <div>
              <p className="text-yellow-800 text-sm font-medium">
                Do not close this window or refresh the page
              </p>
              <p className="text-yellow-700 text-xs mt-1">
                Your subscription is being processed. Closing this window may
                interrupt the payment.
              </p>
            </div>
          </div>
        )}

        <div className="pt-16 pb-8 px-8">
          {paymentSuccess ? (
            <div className="text-center">
              <div className="flex justify-center mb-4">
                <CheckCircle className="text-green-500" size={64} />
              </div>
              <h3 className="text-xl font-medium text-gray-800 mb-2">
                Payment Successful!
              </h3>
              <p className="text-gray-600 mb-6">
                Your subscription has been activated successfully.
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-center text-gray-800 mb-8">
                Complete Your Subscription
              </h2>

              {/* Order Summary */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">
                  Order Summary
                </h3>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Plan</span>
                  <span className="font-medium">{selectedPlan.name}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-600">Amount</span>
                  <span className="font-medium">₹ {selectedPlan.price}/-</span>
                </div>
                <div className="border-t border-gray-200 my-3"></div>
                <div className="flex justify-between">
                  <span className="text-gray-700 font-medium">
                    Total (incl. GST)
                  </span>
                  <span className="font-bold">₹ {selectedPlan.price}/-</span>
                </div>
              </div>

              {/* Error message */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-600 p-3 rounded mb-6 text-sm">
                  {error}
                </div>
              )}

              {/* Success state with payment button */}
              {subscriptionCreated && subscriptionData ? (
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-800 mb-4">
                    Subscription Created Successfully!
                  </h3>
                  <p className="text-gray-600 text-sm mb-6">
                    Please complete your payment to activate your subscription.
                  </p>
                  <button
                    onClick={handlePayment}
                    className="w-full bg-purple-600 text-white rounded-lg py-3 font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
                  >
                    <CreditCard size={16} className="mr-2" /> Pay Now
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleCreateSubscription}
                  disabled={loading}
                  className="w-full bg-purple-600 text-white rounded-lg py-3 font-medium hover:bg-purple-700 transition-colors flex items-center justify-center"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin mr-2" size={16} />
                      Processing...
                    </>
                  ) : (
                    "Proceed to Payment"
                  )}
                </button>
              )}

              <p className="text-xs text-gray-500 text-center mt-6">
                By proceeding, you agree to our Terms of Service and Privacy
                Policy.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCheckout;
